import * as paymentStatementAPI from '@apis/payment-statement';
import * as productAPI from '@apis/product';
import * as salesChannelAPI from '@apis/sales-channels';
import * as tutorClassAPI from '@apis/tutor-classes';
import {
  ClassChangePurchaseItem,
  CommerceChangePurchasesItem,
  PaymentStatementPurchase,
} from '@models/paymentStatement';
import { ProductOption } from '@models/product';
import { SalesChannelsType } from '@models/sales-channels';
import { ClassSchedule } from '@models/schedule';
import useChangeScheduleModalStore from '@stores/changeScheduleModalStore';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getChannelText } from '@utils/string';
import { Button, DatePicker, Descriptions, Input, InputRef, Modal, Select, Table, TimePicker } from 'antd';
import { DescriptionsItemProps } from 'antd/es/descriptions/Item';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import moment from 'moment';
import { Fragment, useEffect, useRef, useState } from 'react';
import './ChangeScheduleModal.scss';
import { combinedRefundItemsByName } from '@utils/payment';

interface ChangeClassScheduleTableProps {
  classInvoiceId: string;
  result?: ClassChangePurchaseItem[];
  onChange: (data: ClassChangePurchaseItem[]) => void;
}
interface ChangeCommerceOptionTableProps {
  productId: string;
  purchases: PaymentStatementPurchase[];
  paidAt: Date;
  result?: CommerceChangePurchasesItem[];
  onChange: (data: CommerceChangePurchasesItem[]) => void;
  channel?: SalesChannelsType;
}

interface CommerceSelectMenuProps {
  productOptionStepList: ProductOption[];
  result?: CommerceChangePurchasesItem[];
  currentName: string;
  currentIndex: number;
  currentUid?: string;
  isRequiredOption?: boolean;
  handleSelectChange: (changeProductOption: ProductOption, currentProductOption: string, currentUid?: string) => void;
}

const CommerceSelectMenu: React.FC<CommerceSelectMenuProps> = (props) => {
  const { productOptionStepList, result, currentName, currentIndex, currentUid, handleSelectChange, isRequiredOption } =
    props;
  const [items, setItems] = useState<ProductOption[]>([]);
  const customInputRef = useRef<InputRef>(null);

  useEffect(() => {
    setItems(productOptionStepList);
  }, [productOptionStepList]);

  const handleAddButton = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    e.preventDefault();
    setItems((prev) => {
      const newValue = [...prev];
      const inputValue = customInputRef.current?.input?.value.trim();
      if (inputValue) {
        const checkIsExist = newValue.some((item) => {
          if (item.step.join(' / ') === inputValue) {
            return true;
          }
          return false;
        });
        if (!checkIsExist) {
          const customOption: ProductOption = {
            step: [inputValue],
            type: 'sequential',
            inventory: 0,
            fee: 0,
          };
          return [customOption, ...newValue];
        }
      }
      return newValue;
    });
  };

  return (
    <Select
      disabled={isRequiredOption}
      value={isRequiredOption ? '필수옵션 수정불가' : result?.[currentIndex]?.newItem || ''}
      options={items.map((item) => ({
        label: (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
            <div>{item.step.join(' / ')}</div>
            <div style={{ fontWeight: 'bold' }}>(재고: {item?.inventory || 0})</div>
          </div>
        ),
        value: JSON.stringify(item),
      }))}
      popupMatchSelectWidth={false}
      style={{ minWidth: 250 }}
      onChange={(e) => handleSelectChange(JSON.parse(e), currentName, currentUid)}
      dropdownRender={(option) => (
        <Fragment>
          {option}
          <div style={{ display: 'flex', gap: '4px' }}>
            <Input placeholder="커스텀 상품 등록" ref={customInputRef} onKeyDown={(e) => e.stopPropagation()} />
            <Button type="text" onClick={handleAddButton}>
              추가
            </Button>
          </div>
        </Fragment>
      )}
    />
  );
};

const ChangeClassScheduleTable: React.FC<ChangeClassScheduleTableProps> = (props) => {
  const { classInvoiceId, result, onChange } = props;

  const scheduleQuery = useQuery({
    queryKey: [`schedule-${classInvoiceId}`],
    queryFn: () => tutorClassAPI.getClassSchedulesByClassInvoiceId(classInvoiceId),
  });

  useEffect(() => {
    const data = scheduleQuery.data?.data;

    if (data && data.length !== 0) {
      onChange(Array.from({ length: data.length }, (_, index) => ({ currentItem: data[index], newItem: '' })));
    }
  }, [scheduleQuery.data?.data]);

  const handleTimeChange = (time: string, index: number, type: 'date' | 'time', dateRegex: RegExp) => {
    if (!time || !result) return;

    if (dateRegex.test(result[index].newItem)) {
      const newResult = [...result];
      newResult[index].newItem = newResult[index].newItem.replace(dateRegex, time);

      onChange(newResult);
    } else {
      const newResult = [...result];

      if (newResult[index].newItem) {
        if (type === 'date') {
          newResult[index].newItem = `${time} ${newResult[index].newItem}`;
        } else {
          newResult[index].newItem = `${newResult[index].newItem} ${time}`;
        }
      } else {
        newResult[index].newItem = moment(newResult[index].currentItem.startDate).format('YYYY-MM-DD HH:mm');
        newResult[index].newItem = newResult[index].newItem.replace(dateRegex, time);
      }

      onChange(newResult);
    }
  };

  const CLASS_SCHEDULE_COLUMNS: ColumnsType<ClassSchedule> = [
    {
      width: 300,
      title: '수업 일시',
      align: 'center',
      dataIndex: 'startDate',
      render: (startDate) => moment(startDate).format('YYYY. MM. DD. HH:mm'),
    },
    {
      title: '변경 수업 일시',
      align: 'center',
      children: [
        {
          width: 200,
          title: '날짜',
          align: 'center',
          dataIndex: 'startDate',
          render: (startDate: string, _, index) => {
            const regex = /\d{4}-\d{1,2}-\d{1,2}/;
            const date = result?.[index]?.newItem.match(regex) || moment(startDate).format().match(regex) || [];

            return (
              <DatePicker
                value={dayjs(date[0])}
                onChange={(e) => handleTimeChange(e?.format('YYYY-MM-DD') || '', index, 'date', regex)}
              />
            );
          },
        },
        {
          width: 150,
          title: '시간',
          align: 'center',
          dataIndex: 'startDate',
          render: (startDate: string, _, index) => {
            const regex = /T?\d{1,2}:\d{1,2}/;
            const date = result?.[index]?.newItem.match(regex) || moment(startDate).format().match(regex) || [];

            return (
              <TimePicker
                value={dayjs(date[0]?.replace('T', '') || '00:00', 'HH:mm')}
                format="HH:mm"
                minuteStep={5}
                onChange={(e) => handleTimeChange(e?.format('HH:mm') || '', index, 'time', regex)}
              />
            );
          },
        },
      ],
    },
  ];

  return (
    <Table
      title={() => '수업 일정 목록'}
      pagination={false}
      bordered
      columns={CLASS_SCHEDULE_COLUMNS}
      dataSource={scheduleQuery.data?.data}
    />
  );
};

const ChangeCommerceOptionTable: React.FC<ChangeCommerceOptionTableProps> = (props) => {
  const { productId, purchases, result, onChange, channel } = props;
  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);

  const productOptionQuery = useQuery({
    queryKey: [`product-options-${productId}`],
    queryFn: () => productAPI.getProductOptions(productId),
  });

  useEffect(() => {
    if (productOptionQuery.data?.data && productOptionQuery.data?.data?.length > 0) {
      setProductOptions(productOptionQuery.data?.data);
    }
  }, [productOptionQuery.data?.data]);

  useEffect(() => {
    onChange(
      Array.from({ length: purchases.length }, (_, index) => ({
        currentItem: purchases[index],
        newItem: '',
      })),
    );
  }, [purchases]);

  const handleSelectChange = (
    changeProductOption: ProductOption,
    currentProductOption: string,
    currentUid?: string,
  ) => {
    if (!result) return;
    const newResult = [...result];

    result.forEach((item, index) => {
      if (currentUid) {
        if (currentUid === item?.currentItem?.uid) {
          newResult[index].newItem = changeProductOption?.step.join(' / ');
        }
      } else {
        if (item.currentItem.name === currentProductOption) {
          newResult[index].newItem = changeProductOption?.step.join(' / ');
        }
      }
    });

    onChange(newResult);
  };

  const COMMERCE_SCHEDULE_COLUMS: ColumnsType<PaymentStatementPurchase> = [
    {
      width: 300,
      title: '수업 일시',
      align: 'center',
      render: (record) => record.name,
    },
    {
      title: '변경할 수업 일시',
      align: 'center',
      render: (record, _record, index) => (
        <CommerceSelectMenu
          productOptionStepList={productOptions}
          result={result}
          currentName={record.name}
          currentIndex={index}
          currentUid={record?.uid}
          isRequiredOption={record?.required}
          handleSelectChange={handleSelectChange}
        />
      ),
    },
  ];

  return (
    <Table
      title={() => {
        channel;
        return <div>수업 일정 목록</div>;
      }}
      pagination={false}
      bordered
      columns={COMMERCE_SCHEDULE_COLUMS}
      dataSource={purchases}
    />
  );
};

const ChangeScheduleModal: React.FC = () => {
  const { data, result, show, close, onChange } = useChangeScheduleModalStore();
  const dialog = useOkCancelDialog();
  const queryClient = useQueryClient();
  const paymentMemoRef = useRef<HTMLTextAreaElement>(null);
  const shippingMemoRef = useRef<HTMLTextAreaElement>(null);
  const qnaMemoRef = useRef<HTMLTextAreaElement>(null);
  const combinedRefundItems = combinedRefundItemsByName(data?.refunds);

  const updatePaymentStatementPlannedDateMutation = useMutation({
    mutationFn: paymentStatementAPI.changeSchedule,
    onSuccess: async () => {
      close();
      dialog.open({
        type: 'ok',
        title: '일정 변경',
        content: '일정 변경에 성공했습니다.',
      });
      await queryClient.invalidateQueries({
        queryKey: [`schedule-${data?.classInvoiceId}`],
      });
      await queryClient.invalidateQueries({
        queryKey: ['admin-search-payment-statement'],
      });
    },
    onError: (e: any) => {
      dialog.open({
        type: 'ok',
        confirmColor: '#FF3D8F',
        title: '일정 변경',
        content: `${e?.response?.data?.message}`,
      });
    },
  });

  const updateSalesChannelOrderPlannedDateMutation = useMutation({
    mutationFn: salesChannelAPI.changePlannedDate,
    onSuccess: async () => {
      close();
      dialog.open({
        type: 'ok',
        title: '일정 변경',
        content: '일정 변경에 성공했습니다.',
      });
      await queryClient.invalidateQueries({
        queryKey: [`schedule-${data?.classInvoiceId}`],
      });
      await queryClient.invalidateQueries({
        queryKey: ['admin-search-payment-statement'],
      });
    },
    onError: (e: any) => {
      dialog.open({
        type: 'ok',
        confirmColor: '#FF3D8F',
        title: '일정 변경',
        content: `${e?.response?.data?.message}`,
      });
    },
  });

  const isSubmitting =
    updatePaymentStatementPlannedDateMutation.isPending || updateSalesChannelOrderPlannedDateMutation.isPending;

  const handleConfirmClick = () => {
    if (isSubmitting) {
      return;
    }

    if (data) {
      const requestBody: paymentStatementAPI.ChangeScheduleBody = {
        body: {
          saleType: data.saleType === 'class' ? 'class' : 'commerce',
          productId: data?.productId,
          changePurchaseItems: [],
          paidAt: data.paidAt,
          memo: {
            qna: data?.memo?.qna === qnaMemoRef.current?.value ? undefined : qnaMemoRef.current?.value,
            payment: data?.memo?.payment === paymentMemoRef.current?.value ? undefined : paymentMemoRef.current?.value,
            shipping:
              data?.memo?.shipping === shippingMemoRef.current?.value ? undefined : shippingMemoRef.current?.value,
          },
        },
      };

      if (result && result.length) {
        if (data!.saleType === 'class') {
          requestBody.body.changePurchaseItems = (result as ClassChangePurchaseItem[]).filter((item) => item.newItem);
        } else {
          requestBody.body.changePurchaseItems = (result as CommerceChangePurchasesItem[]).filter(
            (item) => item.newItem,
          );
        }
      }

      if (data?.channel) {
        requestBody['salesChannelOrderId'] = data?.orderId;
        updateSalesChannelOrderPlannedDateMutation.mutate(requestBody);
      } else {
        requestBody['paymentStatementId'] = data?.orderId;
        updatePaymentStatementPlannedDateMutation.mutate(requestBody);
      }
    } else {
      dialog.open({
        type: 'ok',
        confirmColor: '#FF3D8F',
        title: '일정 변경',
        content: '일정 변경에 실패했습니다\n새로고침 후 다시 시도해주세요.',
      });
    }
  };

  const PURCHASES_INFO_ITEMS: DescriptionsItemProps[] = [
    { span: 3, label: '상품명', children: data?.productName || '-' },
    { span: 3, label: '구매자', children: data?.buyerName || '-' },
    {
      span: 3,
      label: '구매옵션',
      children: data?.purchases?.map((item) => <div>{`${item.name} (${item.count})`}</div>) || '-',
    },
    {
      span: 3,
      label: '환불옵션',
      children: combinedRefundItems?.map((item) => <div>{`${item.name} (${item.count})`}</div>) || '-',
    },
    {
      span: 3,
      label: '입력옵션',
      children: <div style={{ whiteSpace: 'pre-wrap' }}>{data?.memo?.qna || '-'}</div>,
    },
    { span: 3, label: '결제금액', children: `${data?.paidAmount.toLocaleString() || 0}원` },
  ];

  return (
    <Modal
      className="schedule-modal"
      open={show}
      width={800}
      onCancel={close}
      okText={isSubmitting ? '저장 중...' : '저장하기'}
      cancelText="닫기"
      confirmLoading={isSubmitting}
      onOk={() => handleConfirmClick()}>
      <Descriptions title={`구매 정보 (${getChannelText(data?.channel)} 결제)`} bordered items={PURCHASES_INFO_ITEMS} />
      {data && data.classInvoiceId && data.saleType === 'class' && (
        <ChangeClassScheduleTable
          key={JSON.stringify(data)}
          classInvoiceId={data.classInvoiceId}
          result={result as ClassChangePurchaseItem[]}
          onChange={onChange}
        />
      )}
      {data && data.saleType === 'commerce' && (
        <ChangeCommerceOptionTable
          key={JSON.stringify(data)}
          productId={data.productId}
          purchases={data.remainPurchases}
          paidAt={data.paidAt}
          result={result as CommerceChangePurchasesItem[]}
          onChange={onChange}
          channel={data?.channel}
        />
      )}
      <section className="schedule-modal__memo">
        <section className="schedule-modal__memo-item">
          <label htmlFor="payment" className="schedule-modal__label">
            입력 옵션 수정
          </label>
          <textarea
            key={data?.memo?.qna}
            id="qna"
            className="schedule-modal__textarea"
            ref={qnaMemoRef}
            defaultValue={data?.memo?.qna}
            rows={6}
            placeholder="(선택) 입력 옵션을 수정해 주세요!"
          />
        </section>
        <section className="schedule-modal__memo-item">
          <label htmlFor="payment" className="schedule-modal__label">
            결제 메모 수정
          </label>
          <textarea
            key={data?.memo?.payment}
            id="payment"
            className="schedule-modal__textarea"
            ref={paymentMemoRef}
            defaultValue={data?.memo?.payment}
            rows={2}
            placeholder="(선택) 결제 메모를 수정해 주세요!"
          />
        </section>
        <section className="schedule-modal__memo-item">
          <label htmlFor="shipping" className="schedule-modal__label">
            배송 메모 수정
          </label>
          <textarea
            key={data?.memo?.shipping}
            id="shipping"
            className="schedule-modal__textarea"
            ref={shippingMemoRef}
            defaultValue={data?.memo?.shipping}
            rows={2}
            placeholder="(선택) 배송 메모를 입력해 주세요!"
          />
        </section>
      </section>
    </Modal>
  );
};

export default ChangeScheduleModal;
